import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Grid,
    TextField,
    Button,
    Avatar,
    Chip,
    Card,
    CardContent,
    IconButton,
    Divider,
    Alert,
    Snackbar,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    LinearProgress
  } from '@mui/material';
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Work as WorkIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  RocketLaunch as BoostIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import DatabaseService, { UserService } from '../../services/DatabaseService';
import CloudinaryService from '../../services/CloudinaryService';
import ApiService from '../../services/api';
import { useCurrency } from '../../context/CurrencyContext';
import { calculateJobSeekerCompletion } from '../../utils/profileCompletion';
import { isBoostActive } from '../../utils/boost';
import ProfileBoostPayment from '../../components/profile/ProfileBoostPayment';

// Normalize Cloudinary URLs for non-image files (e.g., PDFs)
// If a PDF was uploaded under image/video, rewrite to raw delivery
const normalizeDocumentUrl = (url) => {
  if (!url) return url;
  try {
    const isPdf = /\.pdf(\?|$)/i.test(url);
    const isCloudinary = /res\.cloudinary\.com\//i.test(url);
    if (isPdf && isCloudinary) {
      return url.replace(/\/(image|video)\/upload\//, '/raw/upload/');
    }
    return url;
  } catch {
    return url;
  }
};

const Profile = () => {
  const { currentUser, isAdmin, token } = useAuth();
  const location = useLocation();
  const { formatCurrency } = useCurrency();
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    bio: '',
    skills: [],
    experience: [],
    education: [],
    profilePicture: '',
    resume: '',
    portfolio: '',
    linkedinProfile: '',
    githubProfile: '',
    expectedSalary: { min: null, max: null, currency: 'USD' },
  });
  const [isEditing, setIsEditing] = useState(!!(location.state && location.state.openEdit));
  const [newSkill, setNewSkill] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [userBoost, setUserBoost] = useState({ boosted: false, boostExpiresAt: null, boostStatus: null });

  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      if (!currentUser || !token) {
        showSnackbar('Please log in to view your profile.', 'warning');
        return;
      }
      const uid = currentUser.uid || currentUser._id;
      const userData = await DatabaseService.getUserProfile(uid);
      
      // Map backend user schema to local profile state
      const js = userData.jobSeekerProfile || {};
      const locationCity = userData.location?.city || '';

      setProfile({
        firstName: userData.firstName || currentUser?.firstName || '',
        lastName: userData.lastName || currentUser?.lastName || '',
        email: userData.email || currentUser?.email || '',
        phone: userData.phone || '',
        location: locationCity,
        bio: userData.bio || '',
        skills: js.skills || [],
        experience: js.experience || [],
        education: js.education || [],
        profilePicture: userData.avatar || '',
        resume: js.resume || '',
        // Use a single URL string in UI; convert to array on save
        portfolio: Array.isArray(js.portfolio) ? (js.portfolio[0] || '') : (js.portfolio || ''),
        linkedinProfile: userData.socialLinks?.linkedin || '',
        githubProfile: userData.socialLinks?.github || '',
        jobPreferences: {
          jobTypes: js.jobPreferences?.jobTypes || [],
          workLocation: js.jobPreferences?.workLocation || [],
          industries: js.jobPreferences?.industries || [],
          desiredRole: js.jobPreferences?.desiredRole || ''
        },
        expectedSalary: js.expectedSalary || { min: null, max: null, currency: 'USD' }
      });
      // Capture boost state for UI logic
      setUserBoost({
        boosted: !!userData.boosted,
        boostExpiresAt: userData.boostExpiresAt || null,
        boostStatus: userData.boostStatus || null
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      showSnackbar('Failed to load profile. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser, token]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleSave = async () => {
    try {
      setLoading(true);
      // Ensure any in-progress entry is included on save
      const experienceToSave = Array.isArray(profile.experience) ? [...profile.experience] : [];
      if (newExperience.company && newExperience.position) {
        experienceToSave.push({ ...newExperience });
      }
      const educationToSave = Array.isArray(profile.education) ? [...profile.education] : [];
      if (newEducation.institution && newEducation.degree) {
        educationToSave.push({ ...newEducation });
      }

      // Prepare profile data in the correct format for the backend
      // Ensure portfolio is an array for backend mapping
      const portfolioList = Array.isArray(profile.portfolio)
        ? profile.portfolio
        : (profile.portfolio ? [profile.portfolio] : []);

      const profileData = {
        phone: profile.phone,
        personalInfo: {
          firstName: profile.firstName,
          lastName: profile.lastName,
          profilePhoto: profile.profilePicture,
          address: {
            city: profile.location
          }
        },
        bio: profile.bio,
        skills: profile.skills,
        workExperience: experienceToSave,
        education: educationToSave,
        resume: profile.resume,
        portfolio: portfolioList,
        linkedinProfile: profile.linkedinProfile,
        githubProfile: profile.githubProfile,
        jobPreferences: profile.jobPreferences,
        expectedSalary: profile.expectedSalary
      };
      // Debugging: confirm array sizes before save
      console.debug('[Profile Save] experience:', experienceToSave.length,
        'education:', educationToSave.length,
        'jobTypes:', (profile.jobPreferences?.jobTypes || []).join('|'),
        'workLocation:', (profile.jobPreferences?.workLocation || []).join('|'));
      
      if (!currentUser) return;
      const uid = currentUser.uid || currentUser._id;
      await UserService.updateUserProfile(uid, currentUser.role || currentUser.userType || 'jobseeker', profileData);
      
      // Reload profile to ensure data consistency
      await loadProfile();
      
      setIsEditing(false);
      showSnackbar('Profile updated successfully! All changes have been saved to the database.', 'success');
    } catch (error) {
      console.error('Error updating profile:', error);
      showSnackbar('Error updating profile', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    loadProfile();
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setProfile(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const [systemSettings, setSystemSettings] = useState(null);
  const [profileBoostOpen, setProfileBoostOpen] = useState(false);

  const handleBoostProfile = async () => {
    try {
      const uid = currentUser?.uid || currentUser?._id;
      const isAdminSession = !!isAdmin;

      // Require a USER session for boost request when not using admin override
      if (!isAdminSession && !token) {
        showSnackbar('Please log in to boost your profile.', 'warning');
        return;
      }

      if (isAdminSession) {
        await ApiService.boostUserProfile(uid, { override: true });
        showSnackbar('Profile boosted successfully (admin override)', 'success');
        // refresh boost state
        const refreshed = await DatabaseService.getUserProfile(uid);
        setUserBoost({
          boosted: !!refreshed.boosted,
          boostExpiresAt: refreshed.boostExpiresAt || null,
          boostStatus: refreshed.boostStatus || null
        });
        return;
      }

      // Non-admin: Gate behind payment if required by settings
      const settings = systemSettings || (await ApiService.getSystemSettings());
      setSystemSettings(prev => prev || settings);
      const paymentsRequired = !!(settings?.payments?.enableBoostPayments);
      if (paymentsRequired) {
        setProfileBoostOpen(true);
        return;
      } else {
        await ApiService.requestUserBoost(uid);
        showSnackbar('Boost request submitted. Admin will approve shortly.', 'success');
      }
      // refresh boost state
      const refreshed = await DatabaseService.getUserProfile(uid);
      setUserBoost({
        boosted: !!refreshed.boosted,
        boostExpiresAt: refreshed.boostExpiresAt || null,
        boostStatus: refreshed.boostStatus || null
      });
    } catch (err) {
      console.error('Error boosting profile:', err);
      showSnackbar('Failed to process boost request', 'error');
    }
  };

  const handleProfileBoostPaid = async (transaction) => {
    try {
      const uid = currentUser?.uid || currentUser?._id;
      await ApiService.requestUserBoost(uid, { paymentTransactionId: transaction?.transactionId });
      showSnackbar('Payment recorded and boost request submitted.', 'success');
      setProfileBoostOpen(false);
      const refreshed = await DatabaseService.getUserProfile(uid);
      setUserBoost({
        boosted: !!refreshed.boosted,
        boostExpiresAt: refreshed.boostExpiresAt || null,
        boostStatus: refreshed.boostStatus || null
      });
    } catch (error) {
      console.error('Error finalizing profile boost after payment:', error);
      showSnackbar('Payment succeeded but boost request failed. Please contact support.', 'error');
    }
  };

  const canRequestBoost = (() => {
    const active = isBoostActive(userBoost?.boosted, userBoost?.boostExpiresAt);
    const status = userBoost?.boostStatus;
    // Only allow when logged in as USER; admin can use override separately
    const isLoggedInUser = !!token;
    return isLoggedInUser && !active && status !== 'requested' && status !== 'approved';
  })();

  const handleFileUpload = async (event, fieldName) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      // Upload to Cloudinary and save URL
      let folder = 'jobit/uploads';
      if (fieldName === 'resume') folder = 'jobit/cv';
      if (fieldName === 'profilePicture') folder = 'jobit/profile_photos';
      if (fieldName === 'portfolio') folder = 'jobit/portfolio';
      const upload = await CloudinaryService.uploadFile(file, folder);
      handleInputChange(fieldName, upload.url);
      showSnackbar(`${fieldName} uploaded to Cloudinary successfully!`, 'success');
      
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar('Error uploading file', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Inline editors for experience and education when editing
  const [newExperience, setNewExperience] = useState({ company: '', position: '', startDate: '', endDate: '', description: '' });
  const [newEducation, setNewEducation] = useState({ institution: '', degree: '', fieldOfStudy: '', startYear: '', endYear: '' });

  const addExperience = () => {
    const exp = { ...newExperience };
    if (!exp.company || !exp.position) return;
    setProfile(prev => {
      const next = [...(prev.experience || []), { ...exp, current: !!exp.current }];
      try {
        console.debug('[Add Experience] count:', next.length, 'last:', next[next.length - 1]);
      } catch (_) { /* no-op */ }
      return { ...prev, experience: next };
    });
    setNewExperience({ company: '', position: '', startDate: '', endDate: '', description: '' });
  };

  const addEducation = () => {
    const edu = { ...newEducation };
    if (!edu.institution || !edu.degree) return;
    setProfile(prev => {
      const next = [...(prev.education || []), { ...edu }];
      try {
        console.debug('[Add Education] count:', next.length, 'last:', next[next.length - 1]);
      } catch (_) { /* no-op */ }
      return { ...prev, education: next };
    });
    setNewEducation({ institution: '', degree: '', fieldOfStudy: '', startYear: '', endYear: '' });
  };

  const formatDate = (d) => {
    if (!d) return '';
    try {
      const date = new Date(d);
      if (Number.isNaN(date.getTime())) return '';
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
    } catch {
      return '';
    }
  };

  const formatRange = (start, end, current) => {
    const s = formatDate(start);
    const e = current ? 'Present' : formatDate(end);
    return [s, e].filter(Boolean).join(' - ');
  };

  // Options for multi-selects
  const jobTypeOptions = ['Full-time', 'Part-time', 'Contract', 'Freelance', 'Internship'];
  const workLocationOptions = ['Remote', 'On-site', 'Hybrid'];
  const industryOptions = ['Technology', 'Finance', 'Healthcare', 'Education', 'Retail', 'Manufacturing'];

  const completion = calculateJobSeekerCompletion(profile);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon color="primary" />
            My Profile
          </Typography>
          {!isEditing ? (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<BoostIcon />}
                onClick={handleBoostProfile}
                disabled={!canRequestBoost}
              >
                {canRequestBoost ? 'Boost Profile' : 'Boost Pending/Active'}
              </Button>
            </Box>
          ) : (
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={loading}
              >
                Save
              </Button>
              <Button
                variant="outlined"
                startIcon={<CancelIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        {/* Profile Completion Overview */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="h6">Profile Completion</Typography>
              <Typography variant="h6">{completion.percent}%</Typography>
            </Box>
            <LinearProgress variant="determinate" value={completion.percent} sx={{ height: 8, borderRadius: 5 }} />
            {completion.missingItems.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">Remaining to complete:</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                  {completion.missingItems.map((item) => (
                    <Chip key={item.key} label={item.label} variant="outlined" color="warning" />
                  ))}
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <Grid container spacing={4}>
          {/* Profile Picture and Basic Info */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Avatar
                  src={profile.profilePicture || '/logo192.png'}
                  sx={{ width: 120, height: 120, mx: 'auto', mb: 2 }}
                >
                  {profile.firstName?.[0]}{profile.lastName?.[0]}
                </Avatar>
                {isEditing && (
                  <Box sx={{ mb: 2 }}>
                    <input
                      accept="image/*"
                      style={{ display: 'none' }}
                      id="profile-picture-upload"
                      type="file"
                      onChange={(e) => handleFileUpload(e, 'profilePicture')}
                    />
                    <label htmlFor="profile-picture-upload">
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        startIcon={<AddIcon />}
                      >
                        Upload Photo
                      </Button>
                    </label>
                  </Box>
                )}
                <Typography variant="h5" gutterBottom>
                  {profile.firstName} {profile.lastName}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {profile.email}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile.location}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Profile Details */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  value={profile.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  value={profile.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  value={profile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={profile.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  value={profile.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Bio"
                  value={profile.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  disabled={!isEditing}
                />
              </Grid>
              
              {/* Document and Profile Links Section */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Documents & Links
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Resume/CV Upload
                  </Typography>
                  {isEditing ? (
                    <Box>
                      <input
                        accept=".pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        id="resume-upload"
                        type="file"
                        onChange={(e) => handleFileUpload(e, 'resume')}
                      />
                      <label htmlFor="resume-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          startIcon={<AddIcon />}
                          sx={{ mr: 2 }}
                        >
                          Upload Resume
                        </Button>
                      </label>
                      <TextField
                        fullWidth
                        label="Or paste Resume URL"
                        value={profile.resume}
                        onChange={(e) => handleInputChange('resume', e.target.value)}
                        placeholder="https://drive.google.com/..."
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ) : (
                    profile.resume ? (
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <a
                          href={normalizeDocumentUrl(profile.resume)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: 'none' }}
                        >
                          {(() => {
                            try {
                              const u = new URL(normalizeDocumentUrl(profile.resume));
                              const last = u.pathname.split('/').pop();
                              return decodeURIComponent(last || 'Resume');
                            } catch {
                              const clean = normalizeDocumentUrl(profile.resume).split('?')[0];
                              const parts = clean.split('/');
                              return decodeURIComponent(parts.pop() || 'Resume');
                            }
                          })()}
                        </a>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">No resume uploaded</Typography>
                    )
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Portfolio URL"
                  value={profile.portfolio}
                  onChange={(e) => handleInputChange('portfolio', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://yourportfolio.com"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="LinkedIn Profile"
                  value={profile.linkedinProfile}
                  onChange={(e) => handleInputChange('linkedinProfile', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://linkedin.com/in/..."
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GitHub Profile"
                  value={profile.githubProfile}
                  onChange={(e) => handleInputChange('githubProfile', e.target.value)}
                  disabled={!isEditing}
                  placeholder="https://github.com/..."
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Skills Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom>
              Skills
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {profile.skills.map((skill, index) => (
                <Chip
                  key={index}
                  label={skill}
                  onDelete={isEditing ? () => removeSkill(skill) : undefined}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
            {isEditing && (
              <Box display="flex" gap={1} alignItems="center">
                <TextField
                  size="small"
                  label="Add Skill"
                  value={newSkill}
                  onChange={(e) => setNewSkill(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                />
                <IconButton onClick={addSkill} color="primary">
                  <AddIcon />
                </IconButton>
              </Box>
            )}
          </Grid>

          {/* Experience Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkIcon />
              Experience
            </Typography>
            {profile.experience.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No experience added yet.
              </Typography>
            ) : (
              profile.experience.map((exp, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6">{exp.position}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {exp.company} • {formatRange(exp.startDate, exp.endDate, exp.current)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {exp.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            )}
            {isEditing && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                <TextField label="Company" value={newExperience.company} onChange={(e) => setNewExperience({ ...newExperience, company: e.target.value })} />
                <TextField label="Position" value={newExperience.position} onChange={(e) => setNewExperience({ ...newExperience, position: e.target.value })} />
                <TextField type="date" label="Start Date" InputLabelProps={{ shrink: true }} value={newExperience.startDate} onChange={(e) => setNewExperience({ ...newExperience, startDate: e.target.value })} />
                <TextField type="date" label="End Date" InputLabelProps={{ shrink: true }} value={newExperience.endDate} onChange={(e) => setNewExperience({ ...newExperience, endDate: e.target.value })} />
                <TextField label="Description" multiline rows={3} value={newExperience.description} onChange={(e) => setNewExperience({ ...newExperience, description: e.target.value })} />
                <Box>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addExperience}>Add Experience</Button>
                </Box>
              </Box>
            )}
          </Grid>

          {/* Education Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SchoolIcon />
              Education
            </Typography>
            {profile.education.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No education added yet.
              </Typography>
            ) : (
              profile.education.map((edu, index) => (
                <Card key={index} sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6">{edu.degree}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {edu.institution} • {formatRange(edu.startDate, edu.endDate, edu.current)}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {edu.description}
                    </Typography>
                  </CardContent>
                </Card>
              ))
            )}
            {isEditing && (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, mt: 2 }}>
                <TextField label="Institution" value={newEducation.institution} onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })} />
                <TextField label="Degree" value={newEducation.degree} onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })} />
                <TextField label="Field of Study" value={newEducation.fieldOfStudy} onChange={(e) => setNewEducation({ ...newEducation, fieldOfStudy: e.target.value })} />
                <TextField label="Start Year" value={newEducation.startYear} onChange={(e) => setNewEducation({ ...newEducation, startYear: e.target.value })} />
                <TextField label="End Year" value={newEducation.endYear} onChange={(e) => setNewEducation({ ...newEducation, endYear: e.target.value })} />
                <Box>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={addEducation}>Add Education</Button>
                </Box>
              </Box>
            )}
          </Grid>

          {/* Job Preferences Section */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WorkIcon />
              Job Preferences
            </Typography>
            {!isEditing ? (
              <Box>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Desired Role</Typography>
                <Typography variant="body2" color={profile.jobPreferences?.desiredRole ? 'text.primary' : 'text.secondary'}>
                  {profile.jobPreferences?.desiredRole || 'Not specified'}
                </Typography>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Employment Types</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(profile.jobPreferences?.jobTypes || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No preferences</Typography>
                  ) : (
                    profile.jobPreferences.jobTypes.map((t, i) => <Chip key={i} label={t} variant="outlined" />)
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Work Location</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(profile.jobPreferences?.workLocation || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No preferences</Typography>
                  ) : (
                    profile.jobPreferences.workLocation.map((t, i) => <Chip key={i} label={t} variant="outlined" />)
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Industries</Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(profile.jobPreferences?.industries || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No preferences</Typography>
                  ) : (
                    profile.jobPreferences.industries.map((t, i) => <Chip key={i} label={t} variant="outlined" />)
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ mt: 2 }}>Expected Salary</Typography>
                <Typography variant="body2">
                  {profile.expectedSalary?.min || profile.expectedSalary?.max ? (
                    `${profile.expectedSalary?.min != null ? formatCurrency(profile.expectedSalary.min) : ''}${profile.expectedSalary?.min != null && profile.expectedSalary?.max != null ? ' - ' : ''}${profile.expectedSalary?.max != null ? formatCurrency(profile.expectedSalary.max) : ''}`
                  ) : 'Not specified'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Desired Job Role"
                    value={profile.jobPreferences?.desiredRole || ''}
                    onChange={(e) => handleInputChange('jobPreferences', {
                      ...profile.jobPreferences,
                      desiredRole: e.target.value
                    })}
                    placeholder="e.g., Software Developer, Marketing Manager"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="employment-types-label">Employment Types</InputLabel>
                    <Select
                      labelId="employment-types-label"
                      multiple
                      label="Employment Types"
                      value={profile.jobPreferences?.jobTypes || []}
                      onChange={(e) => handleInputChange('jobPreferences', {
                        ...profile.jobPreferences,
                        jobTypes: e.target.value
                      })}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected || []).map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {jobTypeOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="work-location-label">Work Location</InputLabel>
                    <Select
                      labelId="work-location-label"
                      multiple
                      label="Work Location"
                      value={profile.jobPreferences?.workLocation || []}
                      onChange={(e) => handleInputChange('jobPreferences', {
                        ...profile.jobPreferences,
                        workLocation: e.target.value
                      })}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected || []).map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {workLocationOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel id="industries-label">Industries</InputLabel>
                    <Select
                      labelId="industries-label"
                      multiple
                      label="Industries"
                      value={profile.jobPreferences?.industries || []}
                      onChange={(e) => handleInputChange('jobPreferences', {
                        ...profile.jobPreferences,
                        industries: e.target.value
                      })}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {(selected || []).map((value) => (
                            <Chip key={value} label={value} />
                          ))}
                        </Box>
                      )}
                    >
                      {industryOptions.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Expected Salary Min"
                    value={profile.expectedSalary?.min ?? ''}
                    onChange={(e) => handleInputChange('expectedSalary', {
                      ...profile.expectedSalary,
                      min: e.target.value ? Number(e.target.value) : null
                    })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Expected Salary Max"
                    value={profile.expectedSalary?.max ?? ''}
                    onChange={(e) => handleInputChange('expectedSalary', {
                      ...profile.expectedSalary,
                      max: e.target.value ? Number(e.target.value) : null
                    })}
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Paper>

      <ProfileBoostPayment
        open={profileBoostOpen}
        onClose={() => setProfileBoostOpen(false)}
        userId={currentUser?._id || currentUser?.uid}
        isEmployer={false}
        settings={systemSettings}
        onPaid={handleProfileBoostPaid}
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Profile;